import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, ChevronDown, CircleCheck, FileText, GitBranch, ListChecks, MessageSquare, Phone, PhoneMissed,
  Sparkles, Send, StickyNote, MessagesSquare,
} from 'lucide-react';
import { getColleagues, getDealTasks, getFeed, type CardResponse, type CardStage } from '@/api/crmCard';
import { Sheet } from '@/components/Sheet';
import { crmKeys } from '@/hooks/useDealCard';
import {
  dueLabel, feedIcon, formatMoney, latestFeed, personName, stageColor, trackModel, type FeedIcon,
} from '@/lib/dealCard';
import { formatRowTime } from '@/lib/messageModel';

interface Props {
  project: string;
  card: CardResponse;
  contactName: string;
  contactPhone: string | null;
  onStage: () => void;
  onCall: () => void;
  onTask: () => void;
  onMeeting: () => void;
  onClose: () => void;
}

interface BodyProps extends Omit<Props, 'onClose'> {
  /** Четвёртая быстрая кнопка: в переписке — «Чат» (закрыть окно), на отдельном экране сделки — своё. */
  chatAction: { label: string; icon?: typeof Phone; onClick: () => void };
}

const FEED_ICONS: Record<FeedIcon, typeof Phone> = {
  chat: MessageSquare, call: Phone, 'call-missed': PhoneMissed, note: StickyNote, task: ListChecks,
  stage: GitBranch, doc: FileText, touch: Send, other: Sparkles,
};

const TEMPERATURE: Record<string, string> = { hot: 'Горячий', warm: 'Тёплый', cold: 'Холодный' };
const PRIORITY: Record<string, string> = { low: 'Низкий', medium: 'Средний', high: 'Высокий' };

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return <div className="f"><small>{label}</small><div>{value}</div></div>;
}

/**
 * Окно сделки (прототип, dealSheet): название и сумма, полоса стадий воронки из равных отрезков,
 * текущая стадия ▾, ответственный, быстрые действия, поля, открытые задачи, лента касаний.
 * Данные: карточка (GET chatwoot/card) уже в кэше; задачи (calendar/tasks), касания (timeline) и
 * имена коллег (colleagues) грузятся, только когда окно открыто.
 */
export function DealSheet({ onClose, ...rest }: Props) {
  if (!rest.card.deal) return null;
  return (
    <Sheet label="Карточка сделки" tall onClose={onClose}>
      <DealCardBody {...rest} chatAction={{ label: 'Чат', onClick: onClose }} />
    </Sheet>
  );
}

/**
 * Содержимое карточки сделки — одно и то же в окне над перепиской и на отдельном экране сделки
 * (этап 3, сделка без переписки). Рисует заголовок (.sh) и прокручиваемое тело (.sb).
 */
export function DealCardBody({ project, card, contactName, contactPhone, onStage, onCall, onTask, onMeeting, chatAction }: BodyProps) {
  const deal = card.deal;
  const dealId = deal?.id ?? '';
  const tasks = useQuery({ queryKey: crmKeys.tasks(project, dealId), queryFn: () => getDealTasks(dealId), enabled: !!deal, staleTime: 30_000 });
  const feed = useQuery({ queryKey: crmKeys.feed(project, dealId), queryFn: () => getFeed(dealId), enabled: !!deal, staleTime: 30_000 });
  const people = useQuery({ queryKey: crmKeys.colleagues(project), queryFn: getColleagues, staleTime: 10 * 60_000 });
  if (!deal) return null;

  const stages: CardStage[] = card.stages ?? [];
  const stage = deal.stage ?? stages.find((s) => s.id === deal.stageId) ?? null;
  const color = stageColor(stage);
  const title = deal.company?.name || personName(deal.person ?? card.person) || deal.title;
  const owner = deal.ownerId ? people.data?.find((p) => p.id === deal.ownerId) : null;
  const ownerText = !deal.ownerId ? 'без ответственного' : owner ? (owner.me ? 'ваша' : owner.name) : '';
  const person = deal.person ?? card.person ?? null;
  const open = (tasks.data ?? []).filter((t) => !t.done).slice(0, 6);
  const touches = latestFeed(feed.data ?? [], 20);
  const phone = contactPhone || person?.phones?.[0] || null;
  const track = trackModel(stages, deal.stageId);

  const ChatIcon = chatAction.icon ?? MessagesSquare;
  return (
    <>
      <div className="sh">
        <h3>{title}</h3>
        <span className="amt tnum">{formatMoney(deal.amount, deal.currency)}</span>
      </div>
      <div className="sb">
        <div>
          <div className="dtrack" role="img" aria-label={`Стадия ${stage?.name ?? ''}: ${track.filter((t) => t.filled).length} из ${track.length}`}>
            {track.map((t) => <i key={t.id} style={t.filled ? { background: color } : undefined} />)}
          </div>
          <div className="trackrow">
            <button type="button" onClick={onStage} style={{ color }} aria-label={`Стадия: ${stage?.name ?? ''}. Изменить`}>
              {stage?.name ?? 'Стадия'}<ChevronDown aria-hidden />
            </button>
            <span>{[deal.pipeline?.name, ownerText].filter(Boolean).join(', ')}</span>
          </div>
        </div>

        <div className="qa">
          <button type="button" onClick={onCall}><Phone aria-hidden />Позвонить</button>
          <button type="button" onClick={onTask}><ListChecks aria-hidden />Задача</button>
          <button type="button" onClick={onMeeting}><CalendarDays aria-hidden />Встреча</button>
          <button type="button" onClick={chatAction.onClick}><ChatIcon aria-hidden />{chatAction.label}</button>
        </div>

        <div className="fields">
          <Field label="Контакт" value={personName(person) || contactName} />
          <Field label="Телефон" value={phone} />
          <Field label="Почта" value={person?.emails?.[0]} />
          <Field label="Компания" value={deal.company?.name} />
          <Field label="Должность" value={person?.jobTitle} />
          <Field label="Источник" value={deal.source} />
          <Field label="Температура" value={deal.leadTemperature ? TEMPERATURE[deal.leadTemperature] ?? deal.leadTemperature : null} />
          <Field label="Приоритет" value={deal.priority ? PRIORITY[deal.priority] ?? deal.priority : null} />
          <Field label="Ожидаемое закрытие" value={deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString('ru-RU') : null} />
        </div>
        {deal.description && <p className="ddesc">{deal.description}</p>}

        <div>
          <div className="h4">Задачи{open.length > 0 && <span className="n">{open.length}</span>}</div>
          {tasks.isPending && <p className="pnote">Загружаем…</p>}
          {tasks.isError && <p className="pnote" role="alert">Задачи не загрузились: {tasks.error.message}</p>}
          {tasks.data && open.length === 0 && <p className="pnote">Открытых задач нет.</p>}
          {open.map((t) => {
            const due = dueLabel(t.dueDate);
            const time = t.startsAt ? new Date(t.startsAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : null;
            return (
              <div className="ti" key={t.id}>
                <span className="ic"><CircleCheck aria-hidden /></span>
                <span className="tx">{t.title}<small style={due.late ? { color: 'var(--danger)' } : undefined}>{due.text}{time ? ` в ${time}` : ''}</small></span>
              </div>
            );
          })}
        </div>

        <div>
          <div className="h4">Касания{touches.length > 0 && <span className="n">{touches.length}</span>}</div>
          {feed.isPending && <p className="pnote">Загружаем…</p>}
          {feed.isError && <p className="pnote" role="alert">Касания не загрузились: {feed.error.message}</p>}
          {feed.data && touches.length === 0 && <p className="pnote">Пока ничего не было.</p>}
          {touches.map((it) => {
            const Icon = FEED_ICONS[feedIcon(it)];
            return (
              <div className="ti" key={it.id}>
                <span className="ic" style={feedIcon(it) === 'call-missed' ? { color: 'var(--danger)' } : undefined}><Icon aria-hidden /></span>
                <span className="tx">{it.title}<small>{formatRowTime(Math.floor(Date.parse(it.occurredAt) / 1000))}{it.body ? `. ${it.body.slice(0, 80)}` : ''}</small></span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
